<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import {
  bandQualifiesForExtraRelief,
  findIncomeBandById,
} from '@/helpers/claimTaxBack/taxYearConfig'
import type { ClaimTaxPayMethod, ClaimTaxRegion } from '@/types/claimTaxBack'
import { hasResolvedPayMethod } from '@/helpers/claimTaxBack/claimRoute'
import ClaimGuideStepIndex from '@/components/dashboard/claim/ClaimGuideStepIndex.vue'
import ClaimGuideCollapse from '@/components/dashboard/claim/ClaimGuideCollapse.vue'

/**
 * Guide step 1: region, then income bands for that region, then pay method.
 * Questions appear in that order so a first-time paid donor is not shown
 * both English and Scottish bands at once. Continue at the bottom commits
 * the answers. It stays active: a missing pay-tax answer shows an error.
 * Basic-rate bands skip how they pay tax, because there is nothing extra
 * to claim. "Edit" reopens with the saved answers selected. "Not sure"
 * can be saved but does not collapse the step, because the HMRC route
 * cannot resolve yet.
 */
const props = defineProps<{
  stepNumber: number
}>()

const { t } = useI18n()
const { profile, saveTaxStep } = useClaimTaxBack()
const yearLabel = claimTaxYearConfig.yearLabel

const hasTaxDetails = computed(
  () => profile.value?.region != null && profile.value?.incomeBandId != null
)

const savedBand = computed(() => {
  const bandId = profile.value?.incomeBandId
  if (!bandId) return null
  return findIncomeBandById(claimTaxYearConfig, bandId)
})

const savedBandClaimsExtra = computed(() =>
  savedBand.value
    ? bandQualifiesForExtraRelief(savedBand.value, claimTaxYearConfig)
    : false
)

const hasPayAnswer = computed(() =>
  hasResolvedPayMethod(profile.value?.payMethod ?? null)
)

const isComplete = computed(() => {
  if (!hasTaxDetails.value) return false
  if (!savedBandClaimsExtra.value) return true
  return hasPayAnswer.value
})

const isExpanded = ref(!isComplete.value)
const reopenBtn = ref<HTMLButtonElement | null>(null)

const editRegion = ref<ClaimTaxRegion | null>(profile.value?.region ?? null)
const editBandId = ref<string | null>(profile.value?.incomeBandId ?? null)
const editPayMethod = ref<ClaimTaxPayMethod | null>(
  profile.value?.payMethod ?? null
)
const formError = ref<string | null>(null)
const isSaving = ref(false)

const editBands = computed(() =>
  editRegion.value ? claimTaxYearConfig.bands[editRegion.value] : []
)

const editBand = computed(() =>
  editBandId.value
    ? editBands.value.find((band) => band.id === editBandId.value) ?? null
    : null
)

const editBandClaimsExtra = computed(() =>
  editBand.value
    ? bandQualifiesForExtraRelief(editBand.value, claimTaxYearConfig)
    : false
)

const payMethodOptions: { value: ClaimTaxPayMethod; key: string }[] = [
  { value: 'paye', key: 'Paye' },
  { value: 'tax-return', key: 'TaxReturn' },
  { value: 'both', key: 'Both' },
  { value: 'not-sure', key: 'NotSure' },
]

const hydrateDrafts = () => {
  editRegion.value = profile.value?.region ?? null
  editBandId.value = profile.value?.incomeBandId ?? null
  editPayMethod.value = profile.value?.payMethod ?? null
  formError.value = null
}

const onRegionPicked = () => {
  // Bands differ per region; keep a selection only if it still exists.
  if (
    editBandId.value &&
    !editBands.value.some((band) => band.id === editBandId.value)
  ) {
    editBandId.value = null
  }
}

const expand = () => {
  hydrateDrafts()
  isExpanded.value = true
}

const collapse = async () => {
  hydrateDrafts()
  isExpanded.value = false
  await nextTick()
  reopenBtn.value?.focus()
}

const submitTaxStep = async () => {
  formError.value = null
  if (!editRegion.value || !editBandId.value) {
    formError.value = t('Dashboard.ClaimTaxBack.Guide.Tax.ErrorMissing', {
      year: yearLabel,
    })
    return
  }
  if (editBandClaimsExtra.value && !editPayMethod.value) {
    formError.value = t('Dashboard.ClaimTaxBack.Guide.Tax.ErrorMissing', {
      year: yearLabel,
    })
    return
  }
  isSaving.value = true
  try {
    await saveTaxStep({
      region: editRegion.value,
      incomeBandId: editBandId.value,
      payMethod: editBandClaimsExtra.value ? editPayMethod.value : null,
    })
    // "Not sure" is saved but the step stays open until a real route exists.
    if (!editBandClaimsExtra.value || editPayMethod.value !== 'not-sure') {
      await collapse()
    }
  } catch {
    formError.value = t('Dashboard.ClaimTaxBack.Guide.Tax.ErrorSave')
  } finally {
    isSaving.value = false
  }
}

const savedBandLabel = computed(() => {
  const region = profile.value?.region
  const bandId = profile.value?.incomeBandId
  if (!region || !bandId) return ''
  return (
    claimTaxYearConfig.bands[region].find((band) => band.id === bandId)
      ?.label ?? ''
  )
})

const savedRegionLabel = computed(() => {
  const region = profile.value?.region
  if (!region) return ''
  return region === 'scotland'
    ? t('Dashboard.ClaimTaxBack.Checker.RegionScotland')
    : t('Dashboard.ClaimTaxBack.Checker.RegionRuk')
})

const payMethodLabel = computed(() => {
  switch (profile.value?.payMethod) {
    case 'tax-return':
      return t('Dashboard.ClaimTaxBack.Guide.Tax.MethodTaxReturn')
    case 'both':
      return t('Dashboard.ClaimTaxBack.Guide.Tax.MethodBoth')
    case 'paye':
      return t('Dashboard.ClaimTaxBack.Guide.Tax.MethodPaye')
    default:
      return ''
  }
})
</script>

<template>
  <section
    class="sa-claim-guide__step"
    :class="{ 'sa-claim-guide__step--collapsed': !isExpanded }"
    aria-labelledby="claim-guide-tax-heading"
  >
    <div class="sa-claim-guide__step-header">
      <ClaimGuideStepIndex :number="props.stepNumber" :done="isComplete" />
      <h3 id="claim-guide-tax-heading" class="sa-claim-guide__step-title">
        <span class="sa-sr-only">
          {{
            t('Dashboard.ClaimTaxBack.Guide.StepLabel', {
              number: props.stepNumber,
            })
          }}.
        </span>
        {{ t('Dashboard.ClaimTaxBack.Guide.Tax.Title') }}
        <span v-if="isComplete" class="sa-sr-only">
          . {{ t('Dashboard.ClaimTaxBack.Guide.StepDone') }}
        </span>
      </h3>
      <button
        v-if="!isExpanded"
        ref="reopenBtn"
        type="button"
        class="sa-claim-link-btn sa-claim-guide__step-edit"
        :aria-expanded="false"
        aria-controls="claim-guide-tax-body"
        @click="expand"
      >
        {{ t('Dashboard.ClaimTaxBack.Guide.Tax.Edit') }}
      </button>
    </div>
    <Transition name="sa-claim-fade">
      <dl v-if="!isExpanded" class="sa-claim-guide__facts">
        <div class="sa-claim-guide__fact">
          <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Tax.FactRegion') }}</dt>
          <dd>{{ savedRegionLabel }}</dd>
        </div>
        <div class="sa-claim-guide__fact">
          <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Tax.FactIncome') }}</dt>
          <dd>{{ savedBandLabel }}</dd>
        </div>
        <div v-if="hasPayAnswer" class="sa-claim-guide__fact">
          <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Tax.FactPay') }}</dt>
          <dd>{{ payMethodLabel }}</dd>
        </div>
      </dl>
    </Transition>

    <ClaimGuideCollapse :show="isExpanded" id="claim-guide-tax-body">
      <p class="sa-claim-guide__step-body">
        {{ t('Dashboard.ClaimTaxBack.Guide.Tax.Body', { year: yearLabel }) }}
      </p>
      <fieldset class="sa-claim-field">
        <legend class="sa-claim-field__legend">
          {{ t('Dashboard.ClaimTaxBack.Checker.RegionLegend') }}
        </legend>
        <p class="sa-claim-field__help" id="claim-guide-region-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.RegionHelp') }}
        </p>
        <div
          class="sa-claim-options sa-claim-options--pair"
          aria-describedby="claim-guide-region-help"
        >
          <label
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': editRegion === 'ruk' }"
          >
            <input
              v-model="editRegion"
              class="sa-sr-only"
              type="radio"
              name="guide-region"
              value="ruk"
              @change="onRegionPicked"
            />
            <span>{{ t('Dashboard.ClaimTaxBack.Checker.RegionRuk') }}</span>
          </label>
          <label
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': editRegion === 'scotland' }"
          >
            <input
              v-model="editRegion"
              class="sa-sr-only"
              type="radio"
              name="guide-region"
              value="scotland"
              @change="onRegionPicked"
            />
            <span>
              {{ t('Dashboard.ClaimTaxBack.Checker.RegionScotland') }}
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset v-if="editRegion" class="sa-claim-field">
        <legend class="sa-claim-field__legend">
          {{
            t('Dashboard.ClaimTaxBack.Checker.IncomeLegend', { year: yearLabel })
          }}
        </legend>
        <p class="sa-claim-field__help" id="claim-guide-income-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.IncomeHelp') }}
        </p>
        <div
          class="sa-claim-options"
          aria-describedby="claim-guide-income-help"
        >
          <label
            v-for="band in editBands"
            :key="band.id"
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': editBandId === band.id }"
          >
            <input
              v-model="editBandId"
              class="sa-sr-only"
              type="radio"
              name="guide-income-band"
              :value="band.id"
            />
            <span>{{ band.label }}</span>
          </label>
        </div>
      </fieldset>

      <fieldset v-if="editRegion && editBandId && editBandClaimsExtra" class="sa-claim-field">
        <legend class="sa-claim-field__legend">
          {{ t('Dashboard.ClaimTaxBack.Guide.Pay.Legend') }}
        </legend>
        <p class="sa-claim-field__help" id="claim-pay-help">
          {{ t('Dashboard.ClaimTaxBack.Guide.Pay.Help', { year: yearLabel }) }}
        </p>
        <div class="sa-claim-options" aria-describedby="claim-pay-help">
          <label
            v-for="option in payMethodOptions"
            :key="option.value"
            class="sa-claim-option sa-claim-option--stacked"
            :class="{
              'sa-claim-option--selected': editPayMethod === option.value,
            }"
          >
            <input
              v-model="editPayMethod"
              class="sa-sr-only"
              type="radio"
              name="guide-pay-method"
              :value="option.value"
            />
            <span class="sa-claim-option__title">
              {{ t(`Dashboard.ClaimTaxBack.Guide.Pay.${option.key}`) }}
            </span>
            <span class="sa-claim-option__desc">
              {{
                t(`Dashboard.ClaimTaxBack.Guide.Pay.${option.key}Desc`, {
                  year: yearLabel,
                })
              }}
            </span>
          </label>
        </div>
      </fieldset>

      <div v-if="editBandClaimsExtra && editPayMethod === 'not-sure'" class="sa-claim-guide__not-sure">
        <p>{{ t('Dashboard.ClaimTaxBack.Guide.Pay.NotSureHelp', { year: yearLabel }) }}</p>
      </div>

      <p v-if="formError" id="claim-guide-tax-error" class="sa-field-error" role="alert">
        {{ formError }}
      </p>

      <div v-if="editRegion && editBandId" class="sa-claim-guide__tax-actions">
        <button
          type="button"
          class="sa-btn-primary sa-btn-primary--compact"
          :disabled="isSaving"
          :aria-busy="isSaving"
          :aria-describedby="formError ? 'claim-guide-tax-error' : undefined"
          @click="submitTaxStep"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Tax.Save') }}
        </button>
        <button
          v-if="isComplete"
          type="button"
          class="sa-claim-link-btn"
          :aria-expanded="true"
          aria-controls="claim-guide-tax-body"
          @click="collapse"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.StepCollapse') }}
        </button>
      </div>
    </ClaimGuideCollapse>
  </section>
</template>
